import React, { useState, useEffect } from "react";
import isValidEmail from "../util/isValidEmail";
import { useAuth } from "../services/hooks/useAuth";
import { useNotifyOnProductAvailability } from "../services/hooks/useNotifyOnProductAvailability";
import { usePhoneNumberInput } from "../services/hooks/usePhoneNumberInput";
import { useCart } from "../services/hooks/useCart";
import { useRedirect } from "../services/hooks/useRedirect";
import { useSnackbar } from "../services/hooks/useSnackbar";
import closeModals from "../util/closeModals";
import SecureTransactionNotice from "./SecureTransactionNotice";

const ProductButtons = React.memo(
  ({ product, showBuyNow = true, showSecureTransactionNotice = true }) => {
    const [isInStock, setIsInStock] = useState();

    const [isAddedToCart, setIsAddedToCart] = useState(false);

    const [shouldNotifyWhenBackInStock, setShouldNotifyWhenBackInStock] =
      useState(false);

    const { user } = useAuth();

    const [notifyEmail, setNotifyEmail] = useState(user?.email ?? "");
    const [notifyEmailError, setNotifyEmailError] = useState();

    const {
      notifyOnProductAvailability,
      notifyOnProductAvailabilityError,
      notifyOnProductAvailabilityStatus,
    } = useNotifyOnProductAvailability();

    const [hasSelectedNotificationOption, setHasSelectedNotificationOption] =
      useState(false);
    const [shouldNotifyByEmail, setShouldNotifyByEmail] = useState(false);

    const { phoneNumber, phoneNumberInput, isPhoneNumberValid } =
      usePhoneNumberInput({
        required: true,
        defaultValue: user?.phoneNumber || "",
      });

    const { addToCart, removeFromCart, isInCart, cartItems } = useCart();

    const { redirect } = useRedirect();

    const { addSnackbar } = useSnackbar();

    const handleNotifyByEmail = (e) => {
      e.preventDefault();

      setHasSelectedNotificationOption(true);
      setShouldNotifyByEmail(true);
    };

    const handleNotifyByPhone = (e) => {
      e.preventDefault();

      setHasSelectedNotificationOption(true);
      setShouldNotifyByEmail(false);
    };

    const handleBack = (e) => {
      e.preventDefault();

      setHasSelectedNotificationOption(false);
    };

    const handleNotifyEmailChange = (e) => {
      setNotifyEmail(e.target.value);

      if (isValidEmail(e.target.value)) {
        setNotifyEmailError();
      }
    };

    const handleNotifyOnAvailability = () => {
      if (!isValidEmail(notifyEmail) && shouldNotifyByEmail) {
        setNotifyEmailError("Please enter a valid email address");

        return;
      }

      if (!isPhoneNumberValid && !shouldNotifyByEmail) {
        return;
      }

      setNotifyEmailError();

      notifyOnProductAvailability({
        productId: product.id,
        email: shouldNotifyByEmail ? notifyEmail : null,
        phoneNumber: shouldNotifyByEmail ? null : phoneNumber,
      });
    };

    const handleAddToCart = (e) => {
      e.preventDefault();

      addToCart({
        product,
        quantity: 1,
      });
    };

    const handleRemoveFromCart = (e) => {
      e.preventDefault();

      removeFromCart({
        productId: product.id,
      });
    };

    const handleBuyNow = (e) => {
      e.preventDefault();

      if (!isAddedToCart) {
        addToCart({
          product,
          quantity: 1,
        });
      }

      closeModals("#modalProduct");

      redirect("/checkout");
    };

    const handleBuyFromBrand = (e) => {
      e.preventDefault();

      window.open(product.buyFromBrandLink, "_blank");
    };

    useEffect(() => {
      if (product) {
        setIsInStock(
          product.inventory.quantity > 0 ||
            product.inventory.policy === "continue"
        );
      }

      return () => {
        setIsInStock();
        setShouldNotifyWhenBackInStock(false);
        setNotifyEmail("");
        setNotifyEmailError();
      };
    }, [product]);

    useEffect(() => {
      if (product) {
        setIsAddedToCart(isInCart(product.id));
      }
    }, [cartItems, product]);

    useEffect(() => {
      if (notifyOnProductAvailabilityStatus === "success") {
        addSnackbar("You'll be notified when the product is back in stock.", {
          type: "success",
        });

        setShouldNotifyWhenBackInStock(false);
        setNotifyEmail("");
      }

      if (notifyOnProductAvailabilityError) {
        addSnackbar(notifyOnProductAvailabilityError, {
          type: "danger",
        });
      }
    }, [notifyOnProductAvailabilityStatus, notifyOnProductAvailabilityError]);

    return (
      <React.Fragment>
        {showSecureTransactionNotice && <SecureTransactionNotice />}

        <div className="form-group mb-0">
          <div className="form-row">
            {isInStock && showBuyNow && (
              <div className="col-12 col-lg">
                <button
                  className="btn btn-dark btn-block text-uppercase mb-2"
                  role="button"
                  type="button"
                  onClick={handleBuyNow}
                >
                  Buy Now
                </button>
              </div>
            )}
            <div
              className={`${
                showBuyNow && isInStock ? "col-12 col-lg-auto" : "col-12"
              }`}
            >
              {isInStock ? (
                <button
                  type="button"
                  role="button"
                  aria-label="add to cart"
                  className="btn btn-block text-uppercase btn-outline-dark mb-2"
                  onClick={
                    isAddedToCart ? handleRemoveFromCart : handleAddToCart
                  }
                >
                  {isAddedToCart ? "Remove from cart" : "Add to Cart"}{" "}
                </button>
              ) : product.buyFromBrandLink ? (
                <button
                  type="button"
                  role="button"
                  aria-label="sold out"
                  className="btn btn-block text-uppercase btn-dark mb-2"
                  onClick={handleBuyFromBrand}
                >
                  Buy from Brand
                </button>
              ) : (
                <button
                  type="button"
                  role="button"
                  aria-label="sold out"
                  className="btn btn-block text-uppercase btn-dark not-allowed mb-2"
                  disabled
                >
                  Out of Stock
                </button>
              )}
            </div>
          </div>
        </div>

        {isInStock && product.buyFromBrandLink && (
          <div className="form-row mt-2">
            <div className="col-12 col-lg">
              <a
                href={product.buyFromBrandLink}
                className="styled-underlined-link font-weight-bold"
                target="_blank"
              >
                Buy from Brand <i className="fa fa-external-link-alt" />
              </a>
            </div>
          </div>
        )}

        {!isInStock && (
          <div className="form-row mt-1">
            <div className="col-12 col-lg">
              <div className="custom-control custom-checkbox">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="notifyWhenBackInStock"
                  checked={shouldNotifyWhenBackInStock}
                  onChange={(e) =>
                    setShouldNotifyWhenBackInStock(e.target.checked)
                  }
                />
                <label
                  className="custom-control-label"
                  htmlFor="notifyWhenBackInStock"
                >
                  Notify me when back in stock
                </label>
              </div>
            </div>
          </div>
        )}

        {shouldNotifyWhenBackInStock && (
          <>
            {!hasSelectedNotificationOption && (
              <div className="row mt-5">
                <div className="col-12">
                  <button
                    className="btn btn-lg btn-outline-dark btn-block text-uppercase"
                    type="button"
                    role="button"
                    onClick={handleNotifyByEmail}
                  >
                    <i className="fa fa-envelope mr-2"></i> Notify me by email
                  </button>{" "}
                  <button
                    className="btn btn-lg btn-outline-dark btn-block text-uppercase"
                    type="button"
                    role="button"
                    onClick={handleNotifyByPhone}
                  >
                    <i className="fa fa-phone mr-2"></i> Notify me by phone
                  </button>
                </div>
              </div>
            )}

            {hasSelectedNotificationOption && (
              <>
                <div className="mt-5 mb-5">
                  <a
                    href="#"
                    className="styled-underlined-link"
                    onClick={handleBack}
                  >
                    <i className="fa fa-arrow-left"></i> back
                  </a>
                </div>

                <div>
                  <div className="input-group mt-3 mb-0">
                    {shouldNotifyByEmail ? (
                      <input
                        type="email"
                        className={`form-control ${
                          notifyEmailError ? "is-invalid" : ""
                        }`}
                        placeholder="Email address"
                        aria-label="Email address"
                        value={notifyEmail}
                        onChange={handleNotifyEmailChange}
                      />
                    ) : (
                      phoneNumberInput
                    )}
                    <div
                      className={`input-group-append ${
                        shouldNotifyByEmail ? "" : "mt-5"
                      }`}
                    >
                      <button
                        type="button"
                        role="button"
                        className="btn btn-dark"
                        onClick={handleNotifyOnAvailability}
                        disabled={
                          notifyOnProductAvailabilityStatus === "pending"
                        }
                      >
                        {notifyOnProductAvailabilityStatus === "pending"
                          ? "Please wait..."
                          : "Notify me"}
                      </button>
                    </div>
                  </div>
                  {notifyEmailError && (
                    <span className="input-error d-block">
                      {notifyEmailError}
                    </span>
                  )}
                  <span className="text-muted d-block">
                    By providing your email address or phone number, you agree
                    to be contacted when the product is back in stock. Standared
                    messages and data rates may apply if you choose to be
                    contacted via a phone number.
                  </span>
                </div>
              </>
            )}
          </>
        )}
      </React.Fragment>
    );
  }
);

export default ProductButtons;
